import type {
  ResolvedRiskStrategy,
  RiskCoefDetail,
  RiskCvSource,
  RiskDlcClass,
  RiskRuleFollows,
  RiskStrategyDefinitions,
  RiskStrategyId,
  RiskStrategyValues,
} from '@gozoki/api-types'

/**
 * Stratégies de risque (spec `SPEC-STRATEGIES.md`, § 3 et § 4.2) : formule pure du coef
 * automatique d'une cellule et résolution de sa stratégie. Le backend, le back-office (« Pourquoi
 * ce coef », aperçu des Réglages) et le script de reprise les appellent tous. Les identifiants, les
 * valeurs de départ et les types vivent dans `@gozoki/api-types` (`forecast-risk-strategies.ts`) ;
 * les constantes numériques de la formule vivent ici, sans dépendance, et y sont réexportées.
 */

/** Bornes du coef automatique et des définitions saisies, deux décimales. */
export const RISK_COEF_BOUNDS = { min: 0.5, max: 2.5, decimals: 2 } as const

/** Effet de la volatilité saisi pour un produit très volatil, en % entier. */
export const RISK_VOLATILE_EFFECT_BOUNDS = { min: -40, max: 40 } as const

/** Bornes de `m = cv / vRef` : très stable (`M_MIN`) et très volatil (`M_MAX`, aussi sans mesure). */
export const RISK_M_MIN = 0.75
export const RISK_M_MAX = 1.5

/**
 * Volatilités de référence, médianes de chaque maille (copie locale du 28/10 : 0,39 en référence ×
 * boutique, 0,20 en catégorie × boutique). La reprise les vérifie sur le réplica de prod ; un écart
 * de plus de 0,03 se corrige par une PR.
 */
export const RISK_VREF_REFERENCE = 0.39
export const RISK_VREF_CATEGORIE = 0.2

/** Stratégie d'une cellule sans aucune règle (§ 4.2). */
export const RISK_DEFAULT_STRATEGY: RiskStrategyId = 'neutre'

/** Constantes du code (pas des réglages), rendues telles quelles par l'API des stratégies. */
export const RISK_STRATEGY_CONSTANTS = {
  coefMin: RISK_COEF_BOUNDS.min,
  coefMax: RISK_COEF_BOUNDS.max,
  coefDecimals: RISK_COEF_BOUNDS.decimals,
  volatileEffectMin: RISK_VOLATILE_EFFECT_BOUNDS.min,
  volatileEffectMax: RISK_VOLATILE_EFFECT_BOUNDS.max,
  mMin: RISK_M_MIN,
  mMax: RISK_M_MAX,
  vRefReference: RISK_VREF_REFERENCE,
  vRefCategorie: RISK_VREF_CATEGORIE,
} as const

export type RiskStrategyConstants = typeof RISK_STRATEGY_CONSTANTS

/** DLC d'un produit sans valeur (`products.dlc_days`, défaut de la colonne). */
const DEFAULT_DLC_DAYS = 2

/** Volatilité de référence de la maille mesurée. */
const vRefOf = (source: 'reference' | 'categorie'): number =>
  source === 'reference' ? RISK_VREF_REFERENCE : RISK_VREF_CATEGORIE

/** Classe de DLC : 2 jours ou moins, 3 jours, 4 jours et plus ; `null` : 2 jours. */
export const dlcClassOf = (dlcDays: number | null | undefined): RiskDlcClass => {
  const days = dlcDays ?? DEFAULT_DLC_DAYS
  if (days <= 2) return 'dlc2'
  return days < 4 ? 'dlc3' : 'dlc4'
}

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value))

/** Arrondi à deux décimales, à l'abri des écritures binaires (1,005 → 1,01). */
const round2 = (value: number) => Math.round((value + Number.EPSILON) * 100) / 100

/** Coef borné entre 0,50 et 2,50 et arrondi à deux décimales, avant le plancher du Capping. */
const boundedCoef = (brut: number) =>
  round2(clamp(brut, RISK_COEF_BOUNDS.min, RISK_COEF_BOUNDS.max))

/** Coef brut : `base × (1 + k × (m − 1))`. */
const brutCoef = (base: number, k: number, m: number) => base * (1 + k * (m - 1))

/** `k` d'une stratégie : l'effet saisi est celui de `m = M_MAX`. */
export const riskVolatilityK = (volatileEffectPct: number): number =>
  volatileEffectPct / 100 / (RISK_M_MAX - 1)

/** Effet déduit pour un produit très stable (`m = M_MIN`), en % : `k × (M_MIN − 1)`. */
export const riskStableEffectPct = (volatileEffectPct: number): number =>
  riskVolatilityK(volatileEffectPct) * (RISK_M_MIN - 1) * 100

export interface RiskStrategyCoefInput {
  definitions: RiskStrategyDefinitions
  strategy: RiskStrategyId
  /** `products.dlc_days`, `null` : 2 jours. */
  dlcDays: number | null
  /** cv de la cellule pour le jour de semaine (fraction), `null` sans mesure. */
  cv: number | null
  /** Maille de la mesure : `reference` ou `categorie` (repli) ; ignorée sans mesure. */
  cvSource: RiskCvSource
  /** Capping du moteur en vigueur : un coef sous 1 est relevé à 1. */
  capping: boolean
}

/**
 * Coef automatique d'une cellule pour un jour couvert (§ 3.2) :
 * `base × (1 + k × (m − 1))`, borné entre 0,50 et 2,50, arrondi à deux décimales, puis relevé à 1
 * si le Capping est actif. Sans mesure de volatilité, `m = M_MAX` (traité comme très volatil).
 */
export const riskStrategyCoef = ({
  definitions,
  strategy,
  dlcDays,
  cv,
  cvSource,
  capping,
}: RiskStrategyCoefInput): RiskCoefDetail => {
  const classe = dlcClassOf(dlcDays)
  const values = definitions.strategies[strategy]
  const base = values[classe]
  const k = riskVolatilityK(values.volatileEffectPct)
  const measure =
    cv !== null && cvSource !== null && Number.isFinite(cv)
      ? { cv, cvSource, vRef: vRefOf(cvSource) }
      : null
  const m = measure === null ? RISK_M_MAX : clamp(measure.cv / measure.vRef, RISK_M_MIN, RISK_M_MAX)
  const brut = brutCoef(base, k, m)
  const bounded = boundedCoef(brut)
  const plancherCapping = capping && bounded < 1
  return {
    strategy,
    classe,
    base,
    k,
    cv: measure?.cv ?? null,
    cvSource: measure?.cvSource ?? null,
    vRef: measure?.vRef ?? null,
    m,
    brut,
    plancherCapping,
    coef: plancherCapping ? 1 : bounded,
  }
}

export const RISK_DLC_CLASSES: readonly RiskDlcClass[] = ['dlc2', 'dlc3', 'dlc4']

/**
 * Une stratégie peut-elle donner un coef sous 1 (avertissement des Réglages, § 3.4) ? Le brut est
 * affine en `m` : ses extrêmes sont en `M_MIN` et `M_MAX`, pour chaque classe de DLC.
 */
export const riskStrategyMayGoBelowOne = (values: RiskStrategyValues): boolean => {
  const k = riskVolatilityK(values.volatileEffectPct)
  return RISK_DLC_CLASSES.some((classe) =>
    [RISK_M_MIN, RISK_M_MAX].some((m) => boundedCoef(brutCoef(values[classe], k, m)) < 1),
  )
}

/** Ce que la résolution lit d'une règle vivante. */
export interface RiskRuleForResolution {
  id: number
  /** `null` pour une ligne de priorité (`follows`). */
  strategy: RiskStrategyId | null
  follows: RiskRuleFollows | null
  followsCategoryId: number | null
}

export interface ResolveRiskStrategyInput {
  storeProduct: RiskRuleForResolution | null
  networkProduct: RiskRuleForResolution | null
  storeCategory: RiskRuleForResolution | null
  networkCategory: RiskRuleForResolution | null
  /**
   * Catégorie racine **courante** du produit, `null` sans catégorie : seules les règles produit le
   * touchent alors, et une priorité ne vaut que si elle porte cette catégorie.
   */
  categoryId: number | null
}

/** La règle si elle porte une stratégie, `null` sinon (absente, ou ligne de priorité). */
const winner = (
  rule: RiskRuleForResolution | null,
  source: ResolvedRiskStrategy['source'],
  priority: ResolvedRiskStrategy['priority'] = null,
): ResolvedRiskStrategy | null =>
  rule === null || rule.strategy === null
    ? null
    : { strategy: rule.strategy, source, ruleId: rule.id, priority }

/**
 * Priorité d'une ligne `follows`, tant que le produit reste dans la catégorie du choix : reclassé,
 * elle ne vaut plus. Seule règle de validité d'une priorité, pour le résolveur et l'analyse des poses.
 */
export const riskPriorityOf = (
  rule: Pick<RiskRuleForResolution, 'follows' | 'followsCategoryId'> | null,
  categoryId: number | null,
): RiskRuleFollows | null =>
  rule !== null && rule.follows !== null && rule.followsCategoryId === categoryId
    ? rule.follows
    : null

/**
 * Stratégie d'une cellule (§ 4.2), chaque entrée étant la règle vivante du nœud ou `null` :
 * 1. boutique × produit à stratégie ;
 * 2. réseau × produit et boutique × catégorie tous deux présents : la priorité enregistrée
 *    (`follows`), sinon la règle produit ;
 * 3. un seul des deux ;
 * 4. réseau × catégorie ;
 * 5. sinon Neutre.
 * Une ligne `follows` n'a d'effet qu'avec les deux côtés présents et tant que le produit reste dans
 * la catégorie du choix (`followsCategoryId`) : reclassé, le défaut s'applique.
 */
export const resolveRiskStrategy = ({
  storeProduct,
  networkProduct,
  storeCategory: storeCategoryRule,
  networkCategory: networkCategoryRule,
  categoryId,
}: ResolveRiskStrategyInput): ResolvedRiskStrategy => {
  const storeCategory = categoryId === null ? null : storeCategoryRule
  const networkCategory = categoryId === null ? null : networkCategoryRule
  const own = winner(storeProduct, 'store_product')
  if (own !== null) return own

  if (networkProduct !== null && storeCategory !== null) {
    const follows = riskPriorityOf(storeProduct, categoryId)
    if (follows === 'store') {
      const store = winner(storeCategory, 'store_category', 'choisie')
      if (store !== null) return store
    }
    const product = winner(
      networkProduct,
      'network_product',
      follows === 'product' ? 'choisie' : 'defaut',
    )
    if (product !== null) return product
  }

  const single =
    winner(networkProduct, 'network_product') ??
    winner(storeCategory, 'store_category') ??
    winner(networkCategory, 'network_category')
  return (
    single ?? { strategy: RISK_DEFAULT_STRATEGY, source: 'defaut', ruleId: null, priority: null }
  )
}
